// ManiDine Restaurant Web — src/pages/PaymentsPage.jsx — Updated: 2026-03-31 13:55 (America/Edmonton)
import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import ManagerShell from "../components/ManagerShell";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import useRestaurantContext from "../hooks/useRestaurantContext";

const parseNumber = (v, fallback = 0) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(parseNumber(value));

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDate = (value) => {
  const d = toDateSafe(value);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmtDateTime = (value) => {
  const d = toDateSafe(value);
  if (!d) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getBatchAnchorDate = (batch) =>
  toDateSafe(batch?.periodEndIso || batch?.periodEnd || batch?.paidAtIso || batch?.createdAtIso);

const batchPeriodLabel = (batch) => {
  if (batch?.periodLabel) return batch.periodLabel;
  const start = toDateSafe(batch?.periodStartIso || batch?.periodStart);
  const end = toDateSafe(batch?.periodEndIso || batch?.periodEnd);
  if (!start || !end) return "—";

  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear) {
    const a = start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const b = end.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${a} – ${b}, ${start.getFullYear()}`;
  }

  const a = start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const b = end.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${a} – ${b}`;
};

const batchShortDateLabel = (batch) => {
  if (!batch) return "No payout yet";
  return batchPeriodLabel(batch);
};

const groupOrdersByDay = (orders) => {
  const map = new Map();

  (orders || []).forEach((order) => {
    const key = String(order?.settledDayKey || "unknown");
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(order);
  });

  return Array.from(map.entries())
    .map(([dayKey, rows]) => {
      const dayDate = toDateSafe(dayKey);
      const gross = rows.reduce((sum, row) => sum + parseNumber(row?.gross), 0);
      const serviceFees = rows.reduce(
        (sum, row) => sum + parseNumber(row?.serviceFeeApplied),
        0
      );
      const stripeFees = rows.reduce(
        (sum, row) => sum + parseNumber(row?.stripeFeeApplied),
        0
      );
      const membershipFees = rows.reduce(
        (sum, row) => sum + parseNumber(row?.membershipFeeApplied),
        0
      );
      const totalDeductions = rows.reduce(
        (sum, row) =>
          sum +
          parseNumber(
            row?.totalDeductions ??
              parseNumber(row?.serviceFeeApplied) +
                parseNumber(row?.stripeFeeApplied) +
                parseNumber(row?.membershipFeeApplied)
          ),
        0
      );
      const net = rows.reduce(
        (sum, row) => sum + parseNumber(row?.netPayout ?? row?.netBeforeMembership),
        0
      );

      return {
        dayKey,
        dayLabel: dayDate
          ? dayDate.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : dayKey,
        rows: rows.sort(
          (a, b) =>
            (toDateSafe(a?.settledAtIso)?.getTime() || 0) -
            (toDateSafe(b?.settledAtIso)?.getTime() || 0)
        ),
        gross,
        serviceFees,
        stripeFees,
        membershipFees,
        totalDeductions,
        net,
      };
    })
    .sort((a, b) => (toDateSafe(b.dayKey)?.getTime() || 0) - (toDateSafe(a.dayKey)?.getTime() || 0));
};

const buildBatchPayoutStatusLabel = (batch) => {
  const rawStatus = String(batch?.status || "").trim();
  const status = rawStatus.toLowerCase();
  const paidAt = toDateSafe(batch?.paidAtIso || batch?.paidAt);
  const scheduledAt = toDateSafe(
    batch?.scheduledTransferAtIso ||
      batch?.transferScheduledAtIso ||
      batch?.payoutScheduledAtIso ||
      batch?.scheduledForIso ||
      batch?.expectedTransferAtIso ||
      batch?.expectedTransferDateIso ||
      batch?.transferDateIso
  );

  if (status === "paid" || status === "transferred") {
    return paidAt ? `Paid • ${fmtDate(paidAt)}` : "Paid";
  }

  if (status.includes("sched")) {
    return scheduledAt ? `Scheduled transfer • ${fmtDate(scheduledAt)}` : "Scheduled transfer";
  }

  if (status === "pending" && scheduledAt) {
    return `Scheduled transfer • ${fmtDate(scheduledAt)}`;
  }

  if (rawStatus) {
    const label = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
    if (paidAt) return `${label} • ${fmtDate(paidAt)}`;
    if (scheduledAt) return `${label} • ${fmtDate(scheduledAt)}`;
    return label;
  }

  return scheduledAt ? `Scheduled transfer • ${fmtDate(scheduledAt)}` : "Pending";
};

const buildStripeStatusTone = (payout) => {
  if (payout?.payoutsEnabled) {
    return {
      bg: "rgba(16, 185, 129, 0.12)",
      border: "rgba(16, 185, 129, 0.24)",
      text: "#047857",
      dot: "#10b981",
    };
  }
  if (payout?.onboardingStatus === "in_progress") {
    return {
      bg: "rgba(245, 158, 11, 0.12)",
      border: "rgba(245, 158, 11, 0.24)",
      text: "#b45309",
      dot: "#f59e0b",
    };
  }
  if (payout?.onboardingStatus === "complete") {
    return {
      bg: "rgba(59, 130, 246, 0.12)",
      border: "rgba(59, 130, 246, 0.24)",
      text: "#1d4ed8",
      dot: "#3b82f6",
    };
  }
  return {
    bg: "rgba(100, 116, 139, 0.10)",
    border: "rgba(100, 116, 139, 0.18)",
    text: "#475569",
    dot: "#64748b",
  };
};

const getSummaryCardsForBatch = (batch, emptySubText) => [
  {
    label: "Pay period",
    value: batch ? batchPeriodLabel(batch) : "—",
    sub: batch ? `${parseNumber(batch.orderCount)} orders` : emptySubText,
  },
  {
    label: "Gross sales",
    value: batch ? formatCurrency(batch.periodGross) : formatCurrency(0),
    sub: "Food subtotal + tax for this pay period",
  },
  {
    label: "Total fees",
    value: batch ? formatCurrency(batch.totalFees) : formatCurrency(0),
    sub: "Membership + $1.99/order + Stripe fees",
  },
  {
    label: "Net payout",
    value: batch ? formatCurrency(batch.payoutAmount) : formatCurrency(0),
    sub: batch?.paidAtIso ? `Paid ${fmtDate(batch.paidAtIso)}` : batch?.status ? String(batch.status) : emptySubText,
  },
];

const PaymentsPage = () => {
  const { restaurantId, loading: restaurantLoading } = useRestaurantContext() || {};
  const functions = useMemo(() => getFunctions(), []);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState("");
  const [batches, setBatches] = useState([]);
  const [olderSelectedBatchId, setOlderSelectedBatchId] = useState("");
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("current");

  const [payout, setPayout] = useState({
    provider: "stripe",
    onboardingStatus: "not_started",
    payoutsEnabled: false,
    stripeAccountId: null,
  });

  const stripeStatusLabel = payout?.payoutsEnabled
    ? "Payouts enabled"
    : payout?.onboardingStatus === "in_progress"
    ? "Setup incomplete"
    : payout?.onboardingStatus === "complete"
    ? "Pending verification"
    : "Not connected";

  const stripeStatusTone = buildStripeStatusTone(payout);

  const api = useMemo(
    () => ({
      listMyPayoutBatches: httpsCallable(functions, "restaurantListMyPayoutBatches"),
      getMyPayoutBatchDetails: httpsCallable(functions, "restaurantGetMyPayoutBatchDetails"),
      createRestaurantStripeExpressOnboardingLink: httpsCallable(
        functions,
        "createRestaurantStripeExpressOnboardingLink"
      ),
      createRestaurantStripeAccountLoginLink: httpsCallable(
        functions,
        "createRestaurantStripeAccountLoginLink"
      ),
    }),
    [functions]
  );

  useEffect(() => {
    if (!restaurantId) return undefined;
    const ref = doc(db, "restaurants", restaurantId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        if (data?.payout) setPayout((prev) => ({ ...prev, ...data.payout }));
      },
      () => {}
    );
    return () => unsub();
  }, [restaurantId]);

  const currentBatch = batches[0] || null;
  const previousBatch = batches[1] || null;
  const olderBatches = useMemo(() => batches.slice(2), [batches]);
  const olderSelectedBatch = useMemo(
    () => olderBatches.find((batch) => batch.batchId === olderSelectedBatchId) || null,
    [olderBatches, olderSelectedBatchId]
  );

  const displayedBatch = useMemo(() => {
    if (activeTab === "current") return currentBatch;
    if (activeTab === "previous") return previousBatch;
    return olderSelectedBatch;
  }, [activeTab, currentBatch, previousBatch, olderSelectedBatch]);

  const displayedBatchId = displayedBatch?.batchId || "";

  const loadBatches = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.listMyPayoutBatches({ limit: 104 });
      const data = res?.data || {};
      const rows = Array.isArray(data?.batches) ? data.batches : [];
      setBatches(rows);

      const nextOlderId = rows[2]?.batchId || "";
      setOlderSelectedBatchId((prev) => {
        if (prev && rows.slice(2).some((b) => b.batchId === prev)) return prev;
        return nextOlderId;
      });

      if (activeTab === "older" && !rows[2]?.batchId) {
        setActiveTab(rows[1]?.batchId ? "previous" : "current");
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || "Could not load payout history.");
      setBatches([]);
      setOlderSelectedBatchId("");
      setSelectedBatchDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantLoading) {
      setLoading(true);
      return;
    }
    if (!restaurantId) {
      setLoading(false);
      setBatches([]);
      setOlderSelectedBatchId("");
      setSelectedBatchDetails(null);
      return;
    }
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, restaurantLoading]);

  useEffect(() => {
    const run = async () => {
      if (!displayedBatchId) {
        setSelectedBatchDetails(null);
        return;
      }

      setDetailsLoading(true);
      setError("");
      try {
        const res = await api.getMyPayoutBatchDetails({ batchId: displayedBatchId });
        const data = res?.data || {};
        setSelectedBatchDetails(data);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Could not load payout details.");
        setSelectedBatchDetails(null);
      } finally {
        setDetailsLoading(false);
      }
    };

    run();
  }, [api, displayedBatchId]);

  const onConnectStripe = async () => {
    try {
      const res = await api.createRestaurantStripeExpressOnboardingLink({});
      const url = res?.data?.url || res?.data;
      if (url) window.location.href = url;
      else alert("Unable to start Stripe onboarding.");
    } catch (e) {
      console.error(e);
      alert(e?.message || "Unable to start Stripe onboarding.");
    }
  };

  const onManageStripe = async () => {
    try {
      const res = await api.createRestaurantStripeAccountLoginLink({});
      const url = res?.data?.url || res?.data;
      if (url) window.location.href = url;
      else alert("Unable to open Stripe account management.");
    } catch (e) {
      console.error(e);
      alert(e?.message || "Unable to open Stripe account management.");
    }
  };

  const summaryCards = useMemo(() => {
    if (activeTab === "current") {
      return getSummaryCardsForBatch(currentBatch, "No completed payout history yet");
    }
    if (activeTab === "previous") {
      return getSummaryCardsForBatch(previousBatch, "No previous pay period yet");
    }
    return getSummaryCardsForBatch(olderSelectedBatch, "Select an older pay period");
  }, [activeTab, currentBatch, previousBatch, olderSelectedBatch]);

  const detailGroups = useMemo(
    () => groupOrdersByDay(selectedBatchDetails?.orders || []),
    [selectedBatchDetails]
  );

  const selectedBatchLedgerRow = useMemo(() => {
    const batch = selectedBatchDetails?.batch;
    if (!batch) return null;

    return {
      periodLabel: displayedBatch ? batchPeriodLabel(displayedBatch) : batchPeriodLabel(batch),
      orderCount: parseNumber(batch?.orderCount),
      restaurantSales: parseNumber(batch?.periodGross),
      deductionsBreakdown: `Service ${formatCurrency(batch?.serviceFeeTotal)} • Stripe ${formatCurrency(
        batch?.stripeFeeTotal
      )} • Membership ${formatCurrency(batch?.membershipFeeCharged)}`,
      totalDeductions: parseNumber(batch?.totalFees),
      netProceeds: parseNumber(batch?.payoutAmount),
      payoutStatus: buildBatchPayoutStatusLabel(batch),
    };
  }, [selectedBatchDetails, displayedBatch]);

  const olderBatchOptions = useMemo(
    () => olderBatches.map((batch) => ({
      value: batch.batchId,
      label: batchPeriodLabel(batch),
    })),
    [olderBatches]
  );

  const renderPeriodTab = (value, label, batch, disabled = false) => {
    const active = activeTab === value;
    return (
      <button
        key={value}
        type="button"
        onClick={() => {
          if (!disabled) setActiveTab(value);
        }}
        disabled={disabled}
        style={{
          border: active ? "1px solid #0f172a" : "1px solid rgba(15, 23, 42, 0.10)",
          background: active ? "#0f172a" : "#ffffff",
          color: active ? "#ffffff" : disabled ? "#94a3b8" : "#0f172a",
          borderRadius: 14,
          padding: "10px 14px",
          fontWeight: 700,
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 2,
          minWidth: 190,
          opacity: disabled ? 0.75 : 1,
        }}
      >
        <span>{label}</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: active ? "rgba(255,255,255,0.82)" : disabled ? "#94a3b8" : "#64748b",
            textAlign: "left",
          }}
        >
          {batchShortDateLabel(batch)}
        </span>
      </button>
    );
  };

  if (restaurantLoading) {
    return (
      <ManagerShell title="Payments" subtitle="View your ManiDine payouts and membership charges.">
        <p className="muted">Loading restaurant…</p>
      </ManagerShell>
    );
  }

  if (!restaurantId) {
    return (
      <ManagerShell title="Payments" subtitle="View your ManiDine payouts and membership charges.">
        <p className="muted">No restaurant found. Please sign in again.</p>
      </ManagerShell>
    );
  }

  return (
    <ManagerShell title="Payments" subtitle="View your ManiDine payouts and membership charges.">
      <div className="reports-section">
        <div
          style={{
            padding: 20,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            borderRadius: 18,
            marginBottom: 16,
            background:
              "linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(255, 255, 255, 1))",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 260, flex: "1 1 320px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 10,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 18, color: "#0f172a" }}>
                  Stripe payouts
                </div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 12px",
                    borderRadius: 999,
                    background: stripeStatusTone.bg,
                    border: `1px solid ${stripeStatusTone.border}`,
                    color: stripeStatusTone.text,
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: stripeStatusTone.dot,
                      display: "inline-block",
                    }}
                  />
                  {stripeStatusLabel}
                </div>
              </div>

              <div style={{ color: "#475569", lineHeight: 1.6, marginBottom: 12 }}>
                This page now focuses on clean pay periods. Use <strong>This pay period</strong>,
                <strong> Last pay period</strong>, or pick an <strong>Older pay period</strong> to review the
                payout and see included orders grouped by day.
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: "rgba(15, 23, 42, 0.04)",
                  }}
                >
                  Weekly payout periods
                </span>
                <span
                  style={{
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: "rgba(15, 23, 42, 0.04)",
                  }}
                >
                  Fees: membership + $1.99/order + Stripe
                </span>
              </div>
            </div>

            <div
              style={{
                minWidth: 260,
                flex: "0 1 340px",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 10,
                padding: 16,
                borderRadius: 16,
                background: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(15, 23, 42, 0.08)",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b" }}>
                Stripe account
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                {stripeStatusLabel}
              </div>
              <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
                {payout?.payoutsEnabled
                  ? "Your payout account is connected and ready to receive Stripe transfers."
                  : payout?.onboardingStatus === "in_progress"
                  ? "Finish Stripe onboarding to enable restaurant payouts."
                  : payout?.onboardingStatus === "complete"
                  ? "Stripe setup is submitted. Payouts will enable once verification is complete."
                  : "Start Stripe onboarding to connect your restaurant payout account."}
              </div>

              {payout?.payoutsEnabled ? (
                <button
                  type="button"
                  onClick={onManageStripe}
                  style={{
                    border: "none",
                    borderRadius: 12,
                    padding: "12px 16px",
                    background: "linear-gradient(135deg, #0f172a, #1e293b)",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: "pointer",
                    boxShadow: "0 10px 20px rgba(15, 23, 42, 0.18)",
                  }}
                >
                  Manage Account in Stripe
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onConnectStripe}
                  style={{
                    border: "none",
                    borderRadius: 12,
                    padding: "12px 16px",
                    background: "linear-gradient(135deg, #635bff, #7c3aed)",
                    color: "#ffffff",
                    fontWeight: 800,
                    fontSize: 14,
                    cursor: "pointer",
                    boxShadow: "0 10px 20px rgba(99, 91, 255, 0.22)",
                  }}
                >
                  Connect to Stripe
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 16,
            alignItems: "stretch",
          }}
        >
          {renderPeriodTab("current", "This pay period", currentBatch, !currentBatch)}
          {renderPeriodTab("previous", "Last pay period", previousBatch, !previousBatch)}
          {renderPeriodTab("older", "Older pay periods", olderSelectedBatch, olderBatches.length === 0)}

          <button
            type="button"
            onClick={loadBatches}
            disabled={loading}
            style={{
              border: "1px solid rgba(15, 23, 42, 0.10)",
              background: "#ffffff",
              color: "#0f172a",
              borderRadius: 12,
              padding: "10px 14px",
              fontWeight: 700,
              cursor: "pointer",
              marginLeft: "auto",
            }}
          >
            {loading ? "Refreshing…" : "Refresh payout history"}
          </button>
        </div>

        {activeTab === "older" && (
          <div
            style={{
              border: "1px solid rgba(15, 23, 42, 0.08)",
              borderRadius: 16,
              background: "#ffffff",
              padding: 16,
              marginBottom: 16,
            }}
          >
            <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>
              Select an older pay period
            </div>
            <select
              value={olderSelectedBatchId}
              onChange={(e) => setOlderSelectedBatchId(e.target.value)}
              style={{
                width: "100%",
                maxWidth: 360,
                border: "1px solid rgba(15, 23, 42, 0.10)",
                background: "#ffffff",
                color: "#0f172a",
                borderRadius: 12,
                padding: "12px 14px",
                fontWeight: 700,
                outline: "none",
              }}
            >
              {olderBatchOptions.length === 0 ? (
                <option value="">No older pay periods yet</option>
              ) : (
                olderBatchOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        {error ? (
          <div
            style={{
              padding: 14,
              borderRadius: 14,
              background: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.18)",
              color: "#991b1b",
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        ) : null}

        {!currentBatch && !previousBatch && olderBatches.length === 0 ? (
          <div
            style={{
              padding: 16,
              borderRadius: 16,
              background: "rgba(15, 23, 42, 0.04)",
              border: "1px solid rgba(15, 23, 42, 0.08)",
              color: "#475569",
              marginBottom: 16,
            }}
          >
            No restaurant payout batches exist yet for this account, so there is no pay period history to show yet.
          </div>
        ) : null}

        <section className="reports-kpi-grid" style={{ marginBottom: 16 }}>
          {summaryCards.map((card) => (
            <div
              key={card.label}
              style={{
                border: "1px solid rgba(15, 23, 42, 0.08)",
                borderRadius: 16,
                padding: 18,
                background: "#ffffff",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "#64748b",
                  fontWeight: 700,
                }}
              >
                {card.label}
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#0f172a",
                  marginTop: 8,
                  lineHeight: 1.3,
                }}
              >
                {card.value}
              </div>
              <div style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
                {card.sub}
              </div>
            </div>
          ))}
        </section>

        {activeTab === "older" && olderBatches.length > 0 ? (
          <div
            style={{
              border: "1px solid rgba(15, 23, 42, 0.08)",
              borderRadius: 18,
              overflow: "hidden",
              background: "#ffffff",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                padding: "14px 16px",
                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                fontWeight: 800,
                color: "#0f172a",
              }}
            >
              Older payout history
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {[
                      "Pay period",
                      "Orders",
                      "Gross sales",
                      "Service fees",
                      "Stripe fees",
                      "Membership",
                      "Net payout",
                      "Status",
                      "Paid at",
                    ].map((label) => (
                      <th
                        key={label}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          fontSize: 12,
                          color: "#64748b",
                          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                          background: "rgba(248, 250, 252, 0.9)",
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {olderBatches.map((batch) => {
                    const active = batch.batchId === olderSelectedBatchId;
                    return (
                      <tr
                        key={batch.batchId}
                        onClick={() => {
                          setActiveTab("older");
                          setOlderSelectedBatchId(batch.batchId);
                        }}
                        style={{
                          cursor: "pointer",
                          background: active ? "rgba(15, 23, 42, 0.04)" : "#ffffff",
                        }}
                      >
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a", fontWeight: 700 }}>
                          {batchPeriodLabel(batch)}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                          {parseNumber(batch.orderCount)}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                          {formatCurrency(batch.periodGross)}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                          {formatCurrency(batch.serviceFeeTotal)}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                          {formatCurrency(batch.stripeFeeTotal)}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                          {formatCurrency(batch.membershipFeeCharged)}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a", fontWeight: 700 }}>
                          {formatCurrency(batch.payoutAmount)}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)" }}>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "6px 10px",
                              borderRadius: 999,
                              background:
                                batch.status === "paid"
                                  ? "rgba(16, 185, 129, 0.12)"
                                  : batch.status === "failed"
                                  ? "rgba(239, 68, 68, 0.12)"
                                  : "rgba(59, 130, 246, 0.12)",
                              color:
                                batch.status === "paid"
                                  ? "#047857"
                                  : batch.status === "failed"
                                  ? "#b91c1c"
                                  : "#1d4ed8",
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {String(batch.status || "unknown")}
                          </span>
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#475569" }}>
                          {batch.paidAtIso ? fmtDate(batch.paidAtIso) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <div
          style={{
            border: "1px solid rgba(15, 23, 42, 0.08)",
            borderRadius: 18,
            overflow: "hidden",
            background: "#ffffff",
          }}
        >
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontWeight: 800, color: "#0f172a" }}>
                {displayedBatch ? batchPeriodLabel(displayedBatch) : "Payout details"}
              </div>
              <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
                {activeTab === "current"
                  ? "This pay period details"
                  : activeTab === "previous"
                  ? "Last pay period details"
                  : "Older pay period details"}
              </div>
            </div>
            {displayedBatch ? (
              <div style={{ color: "#64748b", fontSize: 13 }}>
                Net payout: <strong style={{ color: "#0f172a" }}>{formatCurrency(displayedBatch.payoutAmount)}</strong>
              </div>
            ) : null}
          </div>

          {detailsLoading ? (
            <div style={{ padding: 16, color: "#64748b" }}>Loading payout details…</div>
          ) : !selectedBatchDetails ? (
            <div style={{ padding: 16, color: "#64748b" }}>
              {activeTab === "current"
                ? "No current pay period payout details are available yet."
                : activeTab === "previous"
                ? "No last pay period payout details are available yet."
                : "Select an older pay period above to see order details."}
            </div>
          ) : (
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                }}
              >
                {[
                  ["Gross sales", selectedBatchDetails?.batch?.periodGross],
                  ["Service fees", selectedBatchDetails?.batch?.serviceFeeTotal],
                  ["Stripe fees", selectedBatchDetails?.batch?.stripeFeeTotal],
                  ["Membership fee", selectedBatchDetails?.batch?.membershipFeeCharged],
                  ["Total fees", selectedBatchDetails?.batch?.totalFees],
                  ["Net payout", selectedBatchDetails?.batch?.payoutAmount],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      border: "1px solid rgba(15, 23, 42, 0.08)",
                      borderRadius: 14,
                      padding: 14,
                      background: "rgba(248, 250, 252, 0.7)",
                    }}
                  >
                    <div
                      style={{
                        color: "#64748b",
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontWeight: 700,
                      }}
                    >
                      {label}
                    </div>
                    <div style={{ fontWeight: 800, color: "#0f172a", marginTop: 8 }}>
                      {formatCurrency(value)}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 14,
                  padding: 14,
                  background: "rgba(255, 250, 240, 0.75)",
                  color: "#475569",
                  lineHeight: 1.6,
                }}
              >
                This payout uses the pay period shown above. Fees deducted are the monthly
                membership fee charged in this payout, the $1.99 ManiDine service fee per order,
                and actual Stripe processing fees stored on each order.
              </div>

              {selectedBatchLedgerRow ? (
                <div
                  style={{
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    borderRadius: 16,
                    overflow: "hidden",
                    background: "#ffffff",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 14px",
                      background: "rgba(248, 250, 252, 0.85)",
                      borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                      fontWeight: 800,
                      color: "#0f172a",
                    }}
                  >
                    Payout ledger summary
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
                      <thead>
                        <tr>
                          {[
                            "Pay period",
                            "Orders",
                            "Restaurant sales",
                            "Deductions breakdown",
                            "Total deductions",
                            "Net proceeds",
                            "Payout status",
                          ].map((label) => (
                            <th
                              key={label}
                              style={{
                                textAlign: "left",
                                padding: "10px 12px",
                                fontSize: 12,
                                color: "#64748b",
                                borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                                background: "#ffffff",
                              }}
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a", fontWeight: 700 }}>
                            {selectedBatchLedgerRow.periodLabel}
                          </td>
                          <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                            {selectedBatchLedgerRow.orderCount}
                          </td>
                          <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                            {formatCurrency(selectedBatchLedgerRow.restaurantSales)}
                          </td>
                          <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#475569", minWidth: 280 }}>
                            {selectedBatchLedgerRow.deductionsBreakdown}
                          </td>
                          <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                            {formatCurrency(selectedBatchLedgerRow.totalDeductions)}
                          </td>
                          <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a", fontWeight: 800 }}>
                            {formatCurrency(selectedBatchLedgerRow.netProceeds)}
                          </td>
                          <td style={{ padding: "12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a", minWidth: 190 }}>
                            {selectedBatchLedgerRow.payoutStatus}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {detailGroups.length === 0 ? (
                <div style={{ color: "#64748b" }}>No order details found for this payout batch.</div>
              ) : (
                detailGroups.map((group) => (
                  <div
                    key={group.dayKey}
                    style={{
                      border: "1px solid rgba(15, 23, 42, 0.08)",
                      borderRadius: 16,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "12px 14px",
                        background: "rgba(248, 250, 252, 0.85)",
                        borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>{group.dayLabel}</div>
                      <div style={{ color: "#64748b", fontSize: 13 }}>
                        Gross {formatCurrency(group.gross)} • Service fees {formatCurrency(group.serviceFees)} • Stripe fees {formatCurrency(group.stripeFees)} • Net before membership {formatCurrency(group.net)}
                      </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {[
                              "Order",
                              "Customer",
                              "Settled",
                              "Subtotal",
                              "Tax",
                              "Gross",
                              "Service fee",
                              "Stripe fee",
                              "Net before membership",
                            ].map((label) => (
                              <th
                                key={label}
                                style={{
                                  textAlign: "left",
                                  padding: "10px 12px",
                                  fontSize: 12,
                                  color: "#64748b",
                                  borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                                  background: "#ffffff",
                                }}
                              >
                                {label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((order) => (
                            <tr key={order.orderId}>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a", fontWeight: 700 }}>
                                #{order.orderId.slice(0, 8)}
                              </td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                                {order.customerName || "—"}
                              </td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#475569" }}>
                                {fmtDateTime(order.settledAtIso)}
                              </td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                                {formatCurrency(order.itemSubtotal)}
                              </td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                                {formatCurrency(order.taxAmount)}
                              </td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                                {formatCurrency(order.gross)}
                              </td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                                {formatCurrency(order.serviceFeeApplied)}
                              </td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a" }}>
                                {formatCurrency(order.stripeFeeApplied)}
                              </td>
                              <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(15, 23, 42, 0.06)", color: "#0f172a", fontWeight: 700 }}>
                                {formatCurrency(order.netBeforeMembership)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </ManagerShell>
  );
};

export default PaymentsPage;
